import React, { useState } from 'react';
import './DTCAnalysisPanel.css';

const DTCAnalysisPanel = ({ isVisible, onClose }) => {
  const [selectedDTC, setSelectedDTC] = useState(null);

  // Sample DTC data based on requirements document
  const dtcDatabase = [
    {
      code: 'P0243',
      title: 'Engine Turbocharger/Supercharger Wastegate Solenoid',
      description: 'Low Input - Boost pressure control circuit malfunction',
      severity: 'warning',
      category: 'Powertrain',
      repairSteps: [
        'Check boost pressure sensor for proper operation',
        'Inspect all vacuum hoses and electrical connections',
        'Test wastegate solenoid with multimeter (should show 20-30Ω)',
        'Replace sensor if stalled or reading out of spec',
        'Clear diagnostic code and verify repair with road test'
      ],
      tools: ['Multimeter', 'OBD-II Scanner', 'Vacuum Pump Tester'],
      parts: ['Wastegate Solenoid (P/N: 12345)', 'Vacuum Hose Kit'],
      estimatedTime: '45 minutes',
      difficulty: 'Intermediate',
      safetyNotes: 'Engine must be cool before inspection',
      videoAvailable: true
    },
    {
      code: 'P1003',
      title: 'Right Front Wheel Speed Sensor Circuit Low',
      description: 'Suspension system - ABS sensor malfunction',
      severity: 'warning',
      category: 'Chassis',
      repairSteps: [
        'Inspect wheel speed sensor wiring for damage',
        'Clean sensor mounting area and check air gap (0.5-1.5mm)',
        'Test sensor resistance (900-1400Ω at 20°C)',
        'Check reluctor ring for damage or debris',
        'Replace sensor if readings are out of specification'
      ],
      tools: ['Multimeter', 'Feeler Gauge', 'Wire Brush'],
      parts: ['Wheel Speed Sensor (P/N: ABS-67890)'],
      estimatedTime: '30 minutes',
      difficulty: 'Beginner',
      safetyNotes: 'Vehicle must be on jack stands, wheel chocked',
      videoAvailable: true
    },
    {
      code: 'B10199',
      title: 'Battery Management System Communication Error',
      description: 'HV Battery - CAN bus communication failure',
      severity: 'critical',
      category: 'High Voltage',
      repairSteps: [
        '⚠️ FOLLOW HV SAFETY PROTOCOL BEFORE PROCEEDING',
        'Check CAN-H and CAN-L wiring integrity',
        'Verify BMS module power supply (12V)',
        'Scan for related communication codes',
        'Check termination resistors (60Ω between CAN-H/L)',
        'Update BMS firmware if available',
        'Replace BMS module if communication cannot be restored'
      ],
      tools: ['Oscilloscope', 'CAN Bus Analyzer', 'Class 0 HV Gloves'],
      parts: ['BMS Module (P/N: HV-BMS-001)', 'CAN Bus Terminator'],
      estimatedTime: '2 hours',
      difficulty: 'Expert',
      safetyNotes: 'HIGH VOLTAGE - Certified technicians only',
      videoAvailable: false,
      requiresHVSafety: true
    },
    {
      code: 'P1A08',
      title: 'Battery Temperature Sensor Discrepancy',
      description: 'Temperature sensor reading out of expected range',
      severity: 'warning',
      category: 'Battery',
      repairSteps: [
        'Compare all cell temperature readings',
        'Identify outlier sensor(s)',
        'Check sensor connector and wiring',
        'Verify cooling system operation',
        'Replace faulty temperature sensor',
        'Calibrate BMS after replacement'
      ],
      tools: ['Diagnostic Scanner', 'Infrared Thermometer'],
      parts: ['NTC Temperature Sensor (P/N: TEMP-126)'],
      estimatedTime: '1 hour',
      difficulty: 'Intermediate',
      safetyNotes: 'Wait for battery to reach ambient temperature',
      videoAvailable: true
    }
  ];

  if (!isVisible) return null;

  return (
    <div className="dtc-analysis-overlay fade-in">
      <div className="dtc-panel slide-in-left">
        {/* Header */}
        <div className="dtc-header">
          <div className="dtc-title">
            <span className="dtc-icon">🔍</span>
            <h2>Diagnostic trouble codes</h2>
          </div>
          <button className="dtc-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Content */}
        <div className="dtc-content">
          {!selectedDTC ? (
            /* DTC List View */
            <div className="dtc-list">
              {dtcDatabase.map((dtc) => (
                <div
                  key={dtc.code}
                  className={`dtc-card dtc-${dtc.severity}`}
                  onClick={() => setSelectedDTC(dtc)}
                >
                  <div className="dtc-card-header">
                    <div className="dtc-code-badge">{dtc.code}</div>
                    <div className={`dtc-severity-badge severity-${dtc.severity}`}>
                      {dtc.severity === 'critical' && '🚨'}
                      {dtc.severity === 'warning' && '⚠️'}
                      {dtc.severity === 'info' && 'ℹ️'}
                      {dtc.severity.toUpperCase()}
                    </div>
                  </div>
                  <h4>{dtc.title}</h4>
                  <p className="dtc-description">{dtc.description}</p>
                  <div className="dtc-meta">
                    <span className="dtc-category">{dtc.category}</span>
                    <span className="dtc-action">View Details →</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* DTC Detail View */
            <div className="dtc-detail">
              <button
                className="back-btn"
                onClick={() => setSelectedDTC(null)}
              >
                ← Back to List
              </button>

              <div className={`dtc-detail-header dtc-${selectedDTC.severity}`}>
                <div className="detail-code">{selectedDTC.code}</div>
                <h3>{selectedDTC.title}</h3>
                <p>{selectedDTC.description}</p>
              </div>

              {selectedDTC.requiresHVSafety && (
                <div className="hv-warning-banner pulse-warning">
                  <span className="hv-icon">⚡</span>
                  <div>
                    <strong>HIGH VOLTAGE SYSTEM</strong>
                    <p>Mandatory safety protocol must be followed before proceeding</p>
                  </div>
                </div>
              )}

              {/* Repair Steps */}
              <div className="detail-section">
                <h4>📋 Repair Procedure</h4>
                <ol className="repair-steps">
                  {selectedDTC.repairSteps.map((step, idx) => (
                    <li key={idx} className={step.includes('⚠️') ? 'safety-step' : ''}>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>

              {/* Required Tools */}
              <div className="detail-section">
                <h4>🔧 Required Tools</h4>
                <div className="tools-list">
                  {selectedDTC.tools.map((tool, idx) => (
                    <span key={idx} className="tool-tag">{tool}</span>
                  ))}
                </div>
              </div>

              {/* Required Parts */}
              <div className="detail-section">
                <h4>📦 Required Parts</h4>
                <div className="parts-list">
                  {selectedDTC.parts.map((part, idx) => (
                    <div key={idx} className="part-item">{part}</div>
                  ))}
                </div>
              </div>

              {/* Metadata */}
              <div className="detail-metadata">
                <div className="meta-item">
                  <span className="meta-label">Estimated Time</span>
                  <span className="meta-value">{selectedDTC.estimatedTime}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Difficulty</span>
                  <span className="meta-value">{selectedDTC.difficulty}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Category</span>
                  <span className="meta-value">{selectedDTC.category}</span>
                </div>
              </div>

              {/* Safety Notes */}
              <div className="safety-notes">
                <h4>⚠️ Safety Notes</h4>
                <p>{selectedDTC.safetyNotes}</p>
              </div>

              {/* Video Tutorial */}
              {selectedDTC.videoAvailable && (
                <div className="video-section">
                  <button className="video-btn">
                    ▶ Watch Repair Tutorial Video
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DTCAnalysisPanel;
